import { Rank, type EnemyDef } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { defineEnemy, Motion, Pattern } from '../schema';

/* ═══════════════════════════════════════════════════════════════════════════
   E12-a/b/c — LES TROIS TOURELLES DU BASTION.

   Un seul fichier pour les trois : le registre accepte qu'un `.def.ts` exporte
   un TABLEAU de définitions, et les séparer aurait dispersé sur trois fichiers
   la seule chose qui compte ici — le décalage de 0,6 s entre leurs cadences.

   ── Pourquoi le décalage ──
   Trois armes synchronisées produisent trois murs et deux silences : le joueur
   apprend à compter jusqu'à trois et le Bastion devient trivial. Trois armes
   décalées produisent une pression continue, franchissable en permanence mais
   jamais absente. C'est la différence entre un ennemi qu'on attend et un ennemi
   qu'on gère.

   ── Pourquoi trois armes DIFFÉRENTES ──
   Visée, éventail statique, anneau. Détruire une tourelle ne retire donc pas
   « un tiers de la menace » mais une NATURE de menace, ce qui se ressent
   immédiatement et rend le choix de la cible réellement tactique. Trois
   tourelles identiques auraient posé un choix sans conséquence, c'est-à-dire
   pas un choix.

   ⚠️ 400 PV chacune, contre 600 pour le noyau. La somme des tourelles (1 200)
   dépasse largement le noyau : désarmer coûte plus cher que tuer, et c'est
   voulu. Le joueur pressé a le droit d'ignorer les tourelles — le Bastion
   propose, il n'impose pas. C'est ce qui le distingue d'un boss, et c'est ce
   qui en fait un exercice plutôt qu'un examen.
   ═══════════════════════════════════════════════════════════════════════════ */

const BASE = {
  rank: Rank.Standard,
  hp: 400,
  visualRadius: 18,
  score: 260,
  telegraph: 1.1,
  drops: 1,
  // Les parties sont repositionnées chaque tick sur leur porteur : leur propre
  // trajectoire n'est jamais utilisée. On déclare Linear à vitesse nulle pour
  // que la lecture du fichier ne suggère pas un mouvement autonome.
  motion: { kind: Motion.Linear, speed: 0, amplitude: 0, frequency: 0, holdY: 0 },
} as const;

const tourelles: EnemyDef[] = [
  defineEnemy({
    ...BASE,
    id: 'bastion-tourelle-a',
    emitter: {
      // La menace dirigée : c'est elle qu'on détruit en premier quand on veut
      // pouvoir s'arrêter de bouger.
      pattern: Pattern.Aimed,
      every: 1.8,
      count: 2,
      spreadDeg: 14,
      speed: 440,
      firstDelay: 1.4,
      family: Fam.EnemyPink,
    },
  }),
  defineEnemy({
    ...BASE,
    id: 'bastion-tourelle-b',
    emitter: {
      // La géographie : un éventail statique qui interdit une zone devant le
      // Bastion, indépendamment de la position du joueur.
      pattern: Pattern.Fan,
      every: 1.8,
      count: 4,
      spreadDeg: 52,
      speed: 380,
      firstDelay: 2.0,
      family: Fam.EnemyPurple,
    },
  }),
  defineEnemy({
    ...BASE,
    id: 'bastion-tourelle-c',
    emitter: {
      // Le volume : un anneau lent qui remplit l'espace sans viser personne.
      pattern: Pattern.Ring,
      every: 1.8,
      count: 10,
      spreadDeg: 0,
      speed: 300,
      stepDeg: 13,
      firstDelay: 2.6,
      family: Fam.EnemyRed,
    },
  }),
];

export default tourelles;
